/**
 * MoveFirmModal.jsx — move a contact to a different firm, keeping the old
 * firm's name as previous_firm (same job-change history logic as the trial
 * management merge, IMP-1816). Styled with the shared .merge-modal classes.
 *
 * Props:
 *   contact  — { contact_id, name, email, firm_id, _firm_name }
 *   firms    — full firm list for the combobox
 *   getToken — auth
 *   onClose  — () => void
 *   onMoved  — () => void (called after a successful move; close + refresh)
 */

import { useEffect, useState } from 'react';
import { moveContactFirm, lookupFirmByDomain, createFirm, updateFirm } from '../api/backend.js';
import FirmCombobox from './FirmCombobox.jsx';

export default function MoveFirmModal({ contact, firms = [], getToken, onClose, onMoved }) {
  const [inferred, setInferred] = useState([]);
  const [created, setCreated] = useState([]); // firms created inline this session
  const [firmChoice, setFirmChoice] = useState('');
  const [parentChoice, setParentChoice] = useState(''); // optional parent for inline-created firms (pod shops)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Suggest firms matching the contact's email domain (teammates already in
  // the CRM), mirroring the trial merge dialog's default.
  useEffect(() => {
    let dead = false;
    (async () => {
      const domain = String(contact?.email ?? '').toLowerCase().split('@')[1] ?? '';
      if (!domain) return;
      try {
        const r = await lookupFirmByDomain(getToken, domain);
        if (!dead) setInferred((r?.firms ?? []).filter((f) => f.firm_id !== contact?.firm_id));
      } catch { /* best-effort */ }
    })();
    return () => { dead = true; };
  }, [contact?.contact_id]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose?.(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  async function handleMove() {
    if (!firmChoice) { setError('Pick a firm to move to.'); return; }
    setBusy(true); setError(null);
    try {
      if (isCreatedFirm && parentChoice) {
        await updateFirm(getToken, firmChoice, { parent_firm_id: parentChoice });
      }
      await moveContactFirm(getToken, contact.contact_id, firmChoice);
      onMoved?.({ firm_id: firmChoice, firm_name: newFirm?.firm_name ?? '' });
    } catch (e) { setError(e.message); setBusy(false); }
  }

  // New-firm case (e.g. first contact at a fund we don't track yet): create it
  // inline via the same createFirm path ContactForm uses, then select it.
  async function handleCreateFirm(name) {
    setError(null);
    const firmId = `firm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    try {
      await createFirm(getToken, { firm_id: firmId, firm_name: name });
      setCreated((prev) => [...prev, { firm_id: firmId, firm_name: name, hint: 'new firm' }]);
      setFirmChoice(firmId);
    } catch (e) { setError(`Could not create firm: ${e.message}`); }
  }

  const pinned = [...created, ...inferred.map((f) => ({ ...f, hint: 'matched by email domain' }))];
  const oldFirmName = contact?._firm_name || '—';
  const newFirm = [...pinned, ...firms].find((f) => f.firm_id === firmChoice);
  const isCreatedFirm = created.some((f) => f.firm_id === firmChoice);

  return (
    <div className="merge-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="merge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="merge-modal__title">Move to new firm</div>
        <div style={{ fontSize: 13.5 }}>
          <b>{contact?.name || contact?.email}</b>
          {contact?.email && <span style={{ color: 'var(--text-3)' }}> · {contact.email}</span>}
        </div>
        <div className="merge-modal__row">
          <span className="merge-modal__label">Firm</span>
          <div>
            {firmChoice && (
              <div style={{ fontSize: 12.5, marginBottom: 5 }}>
                <span className="merge-modal__old">{oldFirmName}</span>
                <span className="merge-modal__arrow">→</span>
                <b>{newFirm?.firm_name ?? '—'}</b>
              </div>
            )}
            <FirmCombobox
              firms={firms.filter((f) => f.firm_id !== contact?.firm_id)}
              pinned={pinned}
              value={firmChoice}
              onChange={(id) => setFirmChoice(id)}
              onCreate={handleCreateFirm}
              placeholder="Search firms… (or type a new firm name)"
            />
          </div>
        </div>
        {isCreatedFirm && (
          <div className="merge-modal__row">
            <span className="merge-modal__label">Parent</span>
            <div>
              <FirmCombobox
                firms={firms.filter((f) => f.firm_id !== firmChoice)}
                value={parentChoice}
                onChange={(id) => setParentChoice(id)}
                placeholder="Parent firm (optional — for pods, e.g. Millennium)"
              />
            </div>
          </div>
        )}
        <div className="merge-modal__note">
          The old firm ({oldFirmName}) stays on the contact as historical info (previous firm).
        </div>
        {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
        <div className="merge-modal__footer">
          <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={handleMove} disabled={busy || !firmChoice}>
            {busy ? 'Moving…' : 'Move firm'}
          </button>
        </div>
      </div>
    </div>
  );
}
